/**
 * Utility function to get the correct video URL based on the source
 * Handles:
 * - External URLs (http/https)
 * - CMS uploaded videos (/uploads/)
 * - YouTube URLs (converts to embed format)
 * 
 * @param {string} videoPath - The video path from database
 * @returns {string} - The resolved video URL
 */
export function getVideoUrl(videoPath) {
  if (!videoPath) {
    return ''
  }

  // If it's a full URL (starts with http), return as-is
  if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
    // Check if it's a YouTube watch URL and convert to embed
    if (videoPath.includes('youtube.com/watch')) {
      const videoId = videoPath.split('v=')[1]?.split('&')[0]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    } else if (videoPath.includes('youtu.be/')) {
      const videoId = videoPath.split('youtu.be/')[1]?.split('?')[0]
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
    }
    return videoPath
  }

  // Get CMS API URL from environment variable
  const CMS_API_URL = process.env.REACT_APP_CMS_API_URL || 'https://jriit.beetletz.online'
  
  // Remove /api/public if it's in the URL to get base URL
  const baseUrl = CMS_API_URL.replace('/api/public', '').replace(/\/$/, '')

  // If it's from CMS uploads folder, prepend CMS domain
  if (videoPath.startsWith('/uploads/') || videoPath.startsWith('uploads/')) {
    // Ensure path starts with /
    const normalizedPath = videoPath.startsWith('/') ? videoPath : `/${videoPath}`
    return `${baseUrl}${normalizedPath}`
  }
  
  // If it's a path that starts with / but isn't /assets/, it might be a CMS path
  // Check if it looks like a video file path (has video extension)
  if (videoPath.startsWith('/') && /\.(mp4|webm|ogg|mov|avi|m4v|flv|wmv)$/i.test(videoPath)) {
    return `${baseUrl}${videoPath}`
  }

  // For any other path, return as-is
  return videoPath
}

/**
 * Check if a video URL is a local file (needs video tag) or embed URL (needs iframe)
 * @param {string} videoUrl - The video URL to check
 * @returns {object} - { isLocal: boolean, isYouTube: boolean, url: string }
 */
export function getVideoType(videoUrl) {
  if (!videoUrl) {
    return { isLocal: false, isYouTube: false, url: '' }
  }

  const resolvedUrl = getVideoUrl(videoUrl)
  
  // Check if it's a local video file
  const isLocal = resolvedUrl.includes('/uploads/') || 
                  resolvedUrl.match(/\.(mp4|webm|ogg|mov|avi|m4v|flv|wmv)$/i) ||
                  (resolvedUrl.startsWith('http') && !resolvedUrl.includes('youtube.com') && !resolvedUrl.includes('youtu.be'))
  
  // Check if it's YouTube
  const isYouTube = resolvedUrl.includes('youtube.com/embed') || 
                    resolvedUrl.includes('youtu.be') ||
                    resolvedUrl.includes('youtube.com/watch')

  return {
    isLocal,
    isYouTube,
    url: resolvedUrl
  }
}

